/**
 * useVoiceInput — wraps the browser-native Web Speech API for opt-in voice transcription.
 *
 * Supported: Chrome, Edge, Safari (webkit prefix). Not supported: Firefox (button hidden).
 * No backend required — transcription is entirely client-side.
 *
 * Usage:
 *   const { isListening, supported, start, stop } = useVoiceInput()
 *   // start() fires onTranscript(text) when speech recognition finalises a result
 */
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * BCP-47 tags for the Web Speech API, keyed by our ISO 639-1 codes.
 * The API needs a region ('pl-PL'), not a bare language code, or it falls
 * back to the browser default and transcribes Polish speech as English.
 */
const SPEECH_LOCALES: Record<string, string> = {
  en: 'en-US',
  pl: 'pl-PL',
}

// Web Speech API types — not in TypeScript's standard lib.dom.d.ts yet.
// We declare only the subset this hook uses. Safari requires the webkit prefix.
declare global {
  interface SpeechRecognitionInstance {
    continuous: boolean
    interimResults: boolean
    lang: string
    onresult: ((event: SpeechRecognitionResultEvent) => void) | null
    onend: (() => void) | null
    onerror: (() => void) | null
    start(): void
    stop(): void
    abort(): void
  }
  interface SpeechRecognitionResultEvent {
    readonly results: { [index: number]: { [index: number]: { transcript: string } } }
  }
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance
  }
}

export interface UseVoiceInputResult {
  isListening: boolean
  supported: boolean
  start: (onTranscript: (text: string) => void) => void
  stop: () => void
}

export function useVoiceInput(): UseVoiceInputResult {
  const { i18n } = useTranslation()
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const [isListening, setIsListening] = useState(false)

  const SpeechRecognitionAPI: (new () => SpeechRecognitionInstance) | null =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null)
      : null

  const supported = !!SpeechRecognitionAPI

  const start = useCallback((onTranscript: (text: string) => void) => {
    if (!SpeechRecognitionAPI) return

    // Defensive: stop any running recognition before starting a new one
    if (recognitionRef.current) {
      recognitionRef.current.abort()
    }

    const recognition = new SpeechRecognitionAPI()
    recognition.continuous = false
    recognition.interimResults = false
    // Follow the UI language: a Polish user speaking Polish into an en-US
    // recogniser gets nonsense back.
    recognition.lang = SPEECH_LOCALES[i18n.language] ?? 'en-US'

    recognition.onresult = (event: SpeechRecognitionResultEvent) => {
      const transcript = event.results[0][0].transcript
      onTranscript(transcript)
    }

    recognition.onend = () => setIsListening(false)
    recognition.onerror = () => setIsListening(false)

    recognitionRef.current = recognition
    recognition.start()
    setIsListening(true)
  // SpeechRecognitionAPI reference is stable across renders; the language is
  // read fresh on every start so a mid-session switch takes effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    setIsListening(false)
  }, [])

  return { isListening, supported, start, stop }
}
